import { el, setText, setStyle, damp, ease, clamp01 } from './util.js';

/**
 * Death overlay: visible while `game.death` is truthy. Fully non-interactive
 * — the death cam behind it has to stay readable, so this is a radial
 * vignette only (no `backdrop-filter`, no blur).
 *
 * Defensive against a missing `game` subsystem: with no GameSystem present
 * `death` is always treated as null and the overlay stays hidden.
 */
export class DeathScreen {
  constructor(parent, ctx) {
    this.ctx = ctx;
    this.root = el('div', 'ow-death', parent);
    const inner = el('div', 'ow-death-inner', this.root);

    this.title = el('div', 'ow-death-title', inner, 'ELIMINATED');
    this.sub = el('div', 'ow-death-sub', inner, 'BY HOSTILE FIRE');
    this.count = el('div', 'ow-death-count', inner, 'REDEPLOY IN 0');

    this.shown = 0; // overall fade 0..1
    this.punch = 1; // entrance progress 0..1, restarted on each new death
    this._wasDead = false;
    setStyle(this.root, 'display', 'none');
  }

  update(rawDt) {
    const game = this.ctx.peek('game');
    const death = game?.death ?? null;
    const dead = !!death;

    if (dead && !this._wasDead) this.punch = 0; // fresh death: replay the scale-punch
    this._wasDead = dead;

    // Fade in quickly, and fade out over ~0.25s once death clears.
    this.shown = damp(this.shown, dead ? 1 : 0, 18, rawDt);

    if (dead) {
      this.punch = Math.min(1, this.punch + rawDt / 0.4);
      const killer = death.killer;
      setText(this.sub, killer ? `BY ${String(killer).toUpperCase()}` : 'BY HOSTILE FIRE');
      setText(this.count, `REDEPLOY IN ${Math.max(0, Math.ceil(death.respawnIn ?? 0))}`);
    }

    if (this.shown < 0.004) {
      setStyle(this.root, 'display', 'none');
      return;
    }
    setStyle(this.root, 'display', '');
    setStyle(this.root, 'opacity', this.shown.toFixed(3));
    const s = 0.9 + 0.1 * ease.outBack(clamp01(this.punch));
    setStyle(this.title, 'transform', `scale(${s.toFixed(4)})`);
  }

  dispose() {
    this.root.remove();
  }
}
